import * as i0 from '@angular/core';
import { Component, ViewEncapsulation, ChangeDetectionStrategy, Input, EventEmitter, Output, inject, NgZone, PLATFORM_ID, ViewChild, TemplateRef, Optional, Host, forwardRef, ElementRef, ContentChildren, NgModule } from '@angular/core';
import { Subject, fromEvent, BehaviorSubject, of, combineLatest, merge } from 'rxjs';
import { OverlayModule, CdkOverlayOrigin, CdkConnectedOverlay } from '@angular/cdk/overlay';
import * as i2 from '@angular/cdk/scrolling';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { NgIf, NgTemplateOutlet, isPlatformBrowser, NgSwitch, NgSwitchCase, NgSwitchDefault, NgFor, NgStyle } from '@angular/common';
import * as i8 from 'ng-zorro-antd/core/overlay';
import { NzOverlayModule, getPlacementName, POSITION_MAP } from 'ng-zorro-antd/core/overlay';
import * as i1$3 from 'ng-zorro-antd/empty';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import * as i1 from 'ng-zorro-antd/core/outlet';
import { NzOutletModule } from 'ng-zorro-antd/core/outlet';
import { takeUntil, startWith, distinctUntilChanged, withLatestFrom, map, switchMap } from 'rxjs/operators';
import * as i1$1 from 'ng-zorro-antd/core/services';
import { NzDestroyService } from 'ng-zorro-antd/core/services';
import * as i1$2 from 'ng-zorro-antd/icon';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { __decorate } from 'tslib';
import { InputBoolean, isNotNil, getStatusClassNames } from 'ng-zorro-antd/core/util';
import { BACKSPACE, ESCAPE, TAB, SPACE, ENTER, DOWN_ARROW, UP_ARROW } from '@angular/cdk/keycodes';
import * as i2$1 from '@angular/forms';
import { COMPOSITION_BUFFER_MODE, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { slideMotion } from 'ng-zorro-antd/core/animation';
import * as i2$2 from 'ng-zorro-antd/core/config';
import { WithConfig } from 'ng-zorro-antd/core/config';
import * as i7 from 'ng-zorro-antd/core/form';
import { NzFormPatchModule } from 'ng-zorro-antd/core/form';
import * as i1$5 from 'ng-zorro-antd/core/no-animation';
import { NzNoAnimationDirective } from 'ng-zorro-antd/core/no-animation';
import { cancelRequestAnimationFrame, reqAnimFrame } from 'ng-zorro-antd/core/polyfill';
import * as i1$4 from '@angular/cdk/a11y';
import * as i3 from '@angular/cdk/platform';
import * as i5 from '@angular/cdk/bidi';

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
const _c0 = ["*"];
function NzOptionItemGroupComponent_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(ctx_r0.nzLabel);
  }
}
function NzOptionItemComponent_ng_template_1_ng_template_0_Template(rf, ctx) {}
function NzOptionItemComponent_ng_template_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, NzOptionItemComponent_ng_template_1_ng_template_0_Template, 0, 0, "ng-template", 4);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.template);
  }
}
function NzOptionItemComponent_ng_template_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtext(0);
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵtextInterpolate(ctx_r1.label);
  }
}
function NzOptionItemComponent_div_4_span_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 7);
  }
}
function NzOptionItemComponent_div_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 5);
    i0.ɵɵtemplate(1, NzOptionItemComponent_div_4_span_1_Template, 1, 0, "span", 6);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r3 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", !ctx_r3.icon)("ngIfElse", ctx_r3.icon);
  }
}
function NzOptionContainerComponent_div_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 4);
    i0.ɵɵelement(1, "nz-embed-empty", 5);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("specificContent", ctx_r0.notFoundContent);
  }
}
function NzOptionContainerComponent_ng_template_3_nz_option_item_group_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "nz-option-item-group", 9);
  }
  if (rf & 2) {
    const item_r3 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵproperty("nzLabel", item_r3.groupLabel);
  }
}
function NzOptionContainerComponent_ng_template_3_nz_option_item_2_Template(rf, ctx) {
  if (rf & 1) {
    const _r8 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "nz-option-item", 10);
    i0.ɵɵlistener("itemHover", function NzOptionContainerComponent_ng_template_3_nz_option_item_2_Template_nz_option_item_itemHover_0_listener($event) {
      i0.ɵɵrestoreView(_r8);
      const ctx_r7 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r7.onItemHover($event));
    })("itemClick", function NzOptionContainerComponent_ng_template_3_nz_option_item_2_Template_nz_option_item_itemClick_0_listener($event) {
      i0.ɵɵrestoreView(_r8);
      const ctx_r9 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r9.onItemClick($event));
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const item_r3 = i0.ɵɵnextContext().$implicit;
    const ctx_r5 = i0.ɵɵnextContext();
    i0.ɵɵproperty("icon", ctx_r5.menuItemSelectedIcon)("customContent", item_r3.nzCustomContent)("template", item_r3.template)("grouped", !!item_r3.groupLabel)("disabled", item_r3.nzDisabled || ctx_r5.isMaxLimitReached && !ctx_r5.listOfSelectedValue.includes(item_r3["nzValue"]))("showState", ctx_r5.mode === "tags" || ctx_r5.mode === "multiple")("title", item_r3.nzTitle)("label", item_r3.nzLabel)("compareWith", ctx_r5.compareWith)("activatedValue", ctx_r5.activatedValue)("listOfSelectedValue", ctx_r5.listOfSelectedValue)("value", item_r3.nzValue);
  }
}
function NzOptionContainerComponent_ng_template_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0, 6);
    i0.ɵɵtemplate(1, NzOptionContainerComponent_ng_template_3_nz_option_item_group_1_Template, 1, 1, "nz-option-item-group", 7)(2, NzOptionContainerComponent_ng_template_3_nz_option_item_2_Template, 1, 12, "nz-option-item", 8);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const item_r3 = ctx.$implicit;
    i0.ɵɵproperty("ngSwitch", item_r3.type);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngSwitchCase", "group");
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngSwitchCase", "item");
  }
}
function NzOptionContainerComponent_ng_template_4_Template(rf, ctx) {}
function NzOptionComponent_ng_template_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0);
  }
}
function NzSelectArrowComponent_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "span");
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate2("", ctx_r0.listOfValue.length, " / ", ctx_r0.nzMaxMultipleCount, "");
  }
}
function NzSelectArrowComponent_span_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 4);
  }
}
function NzSelectArrowComponent_ng_template_2_ng_container_0_span_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 9);
  }
}
function NzSelectArrowComponent_ng_template_2_ng_container_0_span_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 10);
  }
}
function NzSelectArrowComponent_ng_template_2_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, NzSelectArrowComponent_ng_template_2_ng_container_0_span_1_Template, 1, 0, "span", 7)(2, NzSelectArrowComponent_ng_template_2_ng_container_0_span_2_Template, 1, 0, "span", 8);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r5 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", !ctx_r5.search);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r5.search);
  }
}
function NzSelectArrowComponent_ng_template_2_ng_template_1_ng_container_0_span_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 12);
  }
  if (rf & 2) {
    const suffixIcon_r11 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵproperty("nzType", suffixIcon_r11);
  }
}
function NzSelectArrowComponent_ng_template_2_ng_template_1_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, NzSelectArrowComponent_ng_template_2_ng_template_1_ng_container_0_span_1_Template, 1, 1, "span", 11);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const suffixIcon_r11 = ctx.$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", suffixIcon_r11);
  }
}
function NzSelectArrowComponent_ng_template_2_ng_template_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, NzSelectArrowComponent_ng_template_2_ng_template_1_ng_container_0_Template, 2, 1, "ng-container", 3);
  }
  if (rf & 2) {
    const ctx_r6 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("nzStringTemplateOutlet", ctx_r6.suffixIcon);
  }
}
function NzSelectArrowComponent_ng_template_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, NzSelectArrowComponent_ng_template_2_ng_container_0_Template, 3, 2, "ng-container", 5)(1, NzSelectArrowComponent_ng_template_2_ng_template_1_Template, 1, 1, "ng-template", null, 6, i0.ɵɵtemplateRefExtractor);
  }
  if (rf & 2) {
    const _r7 = i0.ɵɵreference(2);
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngIf", ctx_r2.showArrow && !ctx_r2.suffixIcon)("ngIfElse", _r7);
  }
}
function NzSelectArrowComponent_ng_container_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(ctx_r4.feedbackIcon);
  }
}
function NzSelectClearComponent_span_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 1);
  }
}
function NzSelectItemComponent_ng_container_0_div_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 4);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(ctx_r2.label);
  }
}
function NzSelectItemComponent_ng_container_0_ng_template_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtext(0);
  }
  if (rf & 2) {
    const ctx_r3 = i0.ɵɵnextContext(2);
    i0.ɵɵtextInterpolate(ctx_r3.label);
  }
}
function NzSelectItemComponent_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, NzSelectItemComponent_ng_container_0_div_1_Template, 2, 1, "div", 2)(2, NzSelectItemComponent_ng_container_0_ng_template_2_Template, 1, 1, "ng-template", null, 3, i0.ɵɵtemplateRefExtractor);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const _r4 = i0.ɵɵreference(3);
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r0.deletable)("ngIfElse", _r4);
  }
}
function NzSelectItemComponent_span_1_span_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 7);
  }
}
function NzSelectItemComponent_span_1_Template(rf, ctx) {
  if (rf & 1) {
    const _r7 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "span", 5);
    i0.ɵɵlistener("click", function NzSelectItemComponent_span_1_Template_span_click_0_listener($event) {
      i0.ɵɵrestoreView(_r7);
      const ctx_r6 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r6.onDelete($event));
    });
    i0.ɵɵtemplate(1, NzSelectItemComponent_span_1_span_1_Template, 1, 0, "span", 6);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", !ctx_r1.removeIcon)("ngIfElse", ctx_r1.removeIcon);
  }
}
const _c1 = a0 => ({
  $implicit: a0
});
function NzSelectPlaceholderComponent_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", ctx_r0.placeholder, " ");
  }
}
const _c2 = ["inputElement"];
const _c3 = ["mirrorElement"];
function NzSelectSearchComponent_span_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 3, 4);
  }
}
function NzSelectTopControlComponent_ng_container_1_nz_select_item_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "nz-select-item", 6);
  }
  if (rf & 2) {
    const ctx_r3 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("deletable", false)("disabled", false)("removeIcon", ctx_r3.removeIcon)("label", ctx_r3.listOfTopItem[0].nzLabel)("contentTemplateOutlet", ctx_r3.customTemplate)("contentTemplateOutletContext", ctx_r3.listOfTopItem[0]);
  }
}
function NzSelectTopControlComponent_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    const _r5 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "nz-select-search", 4);
    i0.ɵɵlistener("isComposingChange", function NzSelectTopControlComponent_ng_container_1_Template_nz_select_search_isComposingChange_1_listener($event) {
      i0.ɵɵrestoreView(_r5);
      const ctx_r4 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r4.isComposingChange($event));
    })("valueChange", function NzSelectTopControlComponent_ng_container_1_Template_nz_select_search_valueChange_1_listener($event) {
      i0.ɵɵrestoreView(_r5);
      const ctx_r6 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r6.onInputValueChange($event));
    });
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(2, NzSelectTopControlComponent_ng_container_1_nz_select_item_2_Template, 1, 6, "nz-select-item", 5);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("nzId", ctx_r0.nzId)("disabled", ctx_r0.disabled)("value", ctx_r0.inputValue)("showInput", ctx_r0.showSearch)("mirrorSync", false)("autofocus", ctx_r0.autofocus)("focusTrigger", ctx_r0.open);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r0.isShowSingleLabel);
  }
}
function NzSelectTopControlComponent_ng_container_2_nz_select_item_1_Template(rf, ctx) {
  if (rf & 1) {
    const _r10 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "nz-select-item", 9);
    i0.ɵɵlistener("delete", function NzSelectTopControlComponent_ng_container_2_nz_select_item_1_Template_nz_select_item_delete_0_listener() {
      const restoredCtx = i0.ɵɵrestoreView(_r10);
      const item_r8 = restoredCtx.$implicit;
      const ctx_r9 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r9.onDeleteItem(item_r8.contentTemplateOutletContext));
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const item_r8 = ctx.$implicit;
    const ctx_r7 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("removeIcon", ctx_r7.removeIcon)("label", item_r8.nzLabel)("disabled", item_r8.nzDisabled || ctx_r7.disabled)("contentTemplateOutlet", item_r8.contentTemplateOutlet)("deletable", true)("contentTemplateOutletContext", item_r8.contentTemplateOutletContext);
  }
}
function NzSelectTopControlComponent_ng_container_2_Template(rf, ctx) {
  if (rf & 1) {
    const _r12 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, NzSelectTopControlComponent_ng_container_2_nz_select_item_1_Template, 1, 6, "nz-select-item", 7);
    i0.ɵɵelementStart(2, "nz-select-search", 8);
    i0.ɵɵlistener("isComposingChange", function NzSelectTopControlComponent_ng_container_2_Template_nz_select_search_isComposingChange_2_listener($event) {
      i0.ɵɵrestoreView(_r12);
      const ctx_r11 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r11.isComposingChange($event));
    })("valueChange", function NzSelectTopControlComponent_ng_container_2_Template_nz_select_search_valueChange_2_listener($event) {
      i0.ɵɵrestoreView(_r12);
      const ctx_r13 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r13.onInputValueChange($event));
    });
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", ctx_r1.listOfSlicedItem)("ngForTrackBy", ctx_r1.trackValue);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("nzId", ctx_r1.nzId)("disabled", ctx_r1.disabled)("value", ctx_r1.inputValue)("autofocus", ctx_r1.autofocus)("showInput", true)("mirrorSync", true)("focusTrigger", ctx_r1.open);
  }
}
function NzSelectTopControlComponent_nz_select_placeholder_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "nz-select-placeholder", 10);
  }
  if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵproperty("placeholder", ctx_r2.placeHolder);
  }
}
function NzSelectComponent_nz_select_arrow_2_ng_template_1_nz_form_item_feedback_icon_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "nz-form-item-feedback-icon", 8);
  }
  if (rf & 2) {
    const ctx_r6 = i0.ɵɵnextContext(3);
    i0.ɵɵproperty("status", ctx_r6.status);
  }
}
function NzSelectComponent_nz_select_arrow_2_ng_template_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, NzSelectComponent_nz_select_arrow_2_ng_template_1_nz_form_item_feedback_icon_0_Template, 1, 1, "nz-form-item-feedback-icon", 7);
  }
  if (rf & 2) {
    const ctx_r4 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("ngIf", ctx_r4.hasFeedback && !!ctx_r4.status);
  }
}
function NzSelectComponent_nz_select_arrow_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "nz-select-arrow", 5);
    i0.ɵɵtemplate(1, NzSelectComponent_nz_select_arrow_2_ng_template_1_Template, 1, 1, "ng-template", null, 6, i0.ɵɵtemplateRefExtractor);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const _r5 = i0.ɵɵreference(2);
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("showArrow", ctx_r1.nzShowArrow)("loading", ctx_r1.nzLoading)("search", ctx_r1.nzOpen && ctx_r1.nzShowSearch)("suffixIcon", ctx_r1.nzSuffixIcon)("feedbackIcon", _r5)("nzMaxMultipleCount", ctx_r1.nzMaxMultipleCount)("listOfValue", ctx_r1.listOfValue)("isMaxTagCountSet", ctx_r1.isMaxTagCountSet);
  }
}
function NzSelectComponent_nz_select_clear_3_Template(rf, ctx) {
  if (rf & 1) {
    const _r8 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "nz-select-clear", 9);
    i0.ɵɵlistener("clear", function NzSelectComponent_nz_select_clear_3_Template_nz_select_clear_clear_0_listener() {
      i0.ɵɵrestoreView(_r8);
      const ctx_r7 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r7.onClearSelection());
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵproperty("clearIcon", ctx_r2.nzClearIcon);
  }
}
function NzSelectComponent_ng_template_4_Template(rf, ctx) {
  if (rf & 1) {
    const _r10 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "nz-option-container", 10);
    i0.ɵɵlistener("keydown", function NzSelectComponent_ng_template_4_Template_nz_option_container_keydown_0_listener($event) {
      i0.ɵɵrestoreView(_r10);
      const ctx_r9 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r9.onKeyDown($event));
    })("itemClick", function NzSelectComponent_ng_template_4_Template_nz_option_container_itemClick_0_listener($event) {
      i0.ɵɵrestoreView(_r10);
      const ctx_r11 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r11.onItemClick($event));
    })("scrollToBottom", function NzSelectComponent_ng_template_4_Template_nz_option_container_scrollToBottom_0_listener() {
      i0.ɵɵrestoreView(_r10);
      const ctx_r12 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r12.nzScrollToBottom.emit());
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r3 = i0.ɵɵnextContext();
    i0.ɵɵclassProp("ant-select-dropdown-placement-bottomLeft", ctx_r3.dropDownPosition === "bottomLeft")("ant-select-dropdown-placement-topLeft", ctx_r3.dropDownPosition === "topLeft")("ant-select-dropdown-placement-bottomRight", ctx_r3.dropDownPosition === "bottomRight")("ant-select-dropdown-placement-topRight", ctx_r3.dropDownPosition === "topRight");
    i0.ɵɵproperty("ngStyle", ctx_r3.nzDropdownStyle)("itemSize", ctx_r3.nzOptionHeightPx)("maxItemLength", ctx_r3.nzOptionOverflowSize)("matchWidth", ctx_r3.nzDropdownMatchSelectWidth)("@slideMotion", "enter")("@.disabled", !!(ctx_r3.noAnimation == null ? null : ctx_r3.noAnimation.nzNoAnimation))("nzNoAnimation", ctx_r3.noAnimation == null ? null : ctx_r3.noAnimation.nzNoAnimation)("listOfContainerItem", ctx_r3.listOfContainerItem)("menuItemSelectedIcon", ctx_r3.nzMenuItemSelectedIcon)("notFoundContent", ctx_r3.nzNotFoundContent)("activatedValue", ctx_r3.activatedValue)("listOfSelectedValue", ctx_r3.listOfValue)("dropdownRender", ctx_r3.nzDropdownRender)("compareWith", ctx_r3.compareWith)("mode", ctx_r3.nzMode)("isMaxLimitReached", ctx_r3.isMaxLimitReached);
  }
}
let NzOptionGroupComponent = /*#__PURE__*/(() => {
  class NzOptionGroupComponent {
    constructor() {
      this.nzLabel = null;
      this.changes = new Subject();
    }
    ngOnChanges() {
      this.changes.next();
    }
    static {
      this.ɵfac = function NzOptionGroupComponent_Factory(t) {
        return new (t || NzOptionGroupComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzOptionGroupComponent,
        selectors: [["nz-option-group"]],
        inputs: {
          nzLabel: "nzLabel"
        },
        exportAs: ["nzOptionGroup"],
        standalone: true,
        features: [i0.ɵɵNgOnChangesFeature, i0.ɵɵStandaloneFeature],
        ngContentSelectors: _c0,
        decls: 1,
        vars: 0,
        template: function NzOptionGroupComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵprojection(0);
          }
        },
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return NzOptionGroupComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzOptionItemGroupComponent = /*#__PURE__*/(() => {
  class NzOptionItemGroupComponent {
    constructor() {
      this.nzLabel = null;
    }
    static {
      this.ɵfac = function NzOptionItemGroupComponent_Factory(t) {
        return new (t || NzOptionItemGroupComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzOptionItemGroupComponent,
        selectors: [["nz-option-item-group"]],
        hostAttrs: [1, "ant-select-item", "ant-select-item-group"],
        inputs: {
          nzLabel: "nzLabel"
        },
        standalone: true,
        features: [i0.ɵɵStandaloneFeature],
        decls: 1,
        vars: 1,
        consts: [[4, "nzStringTemplateOutlet"]],
        template: function NzOptionItemGroupComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵtemplate(0, NzOptionItemGroupComponent_ng_container_0_Template, 2, 1, "ng-container", 0);
          }
          if (rf & 2) {
            i0.ɵɵproperty("nzStringTemplateOutlet", ctx.nzLabel);
          }
        },
        dependencies: [NzOutletModule, i1.NzStringTemplateOutletDirective],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return NzOptionItemGroupComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzOptionItemComponent = /*#__PURE__*/(() => {
  class NzOptionItemComponent {
    constructor(elementRef, ngZone, destroy$) {
      this.elementRef = elementRef;
      this.ngZone = ngZone;
      this.destroy$ = destroy$;
      this.selected = false;
      this.activated = false;
      this.grouped = false;
      this.customContent = false;
      this.template = null;
      this.disabled = false;
      this.showState = false;
      this.label = null;
      this.value = null;
      this.activatedValue = null;
      this.listOfSelectedValue = [];
      this.icon = null;
      this.itemClick = new EventEmitter();
      this.itemHover = new EventEmitter();
    }
    ngOnChanges(changes) {
      const {
        value,
        activatedValue,
        listOfSelectedValue
      } = changes;
      if (value || listOfSelectedValue) {
        this.selected = this.listOfSelectedValue.some(v => this.compareWith(v, this.value));
      }
      if (value || activatedValue) {
        this.activated = this.compareWith(this.activatedValue, this.value);
      }
    }
    ngOnInit() {
      this.ngZone.runOutsideAngular(() => {
        fromEvent(this.elementRef.nativeElement, 'click').pipe(takeUntil(this.destroy$)).subscribe(() => {
          if (!this.disabled) {
            this.ngZone.run(() => this.itemClick.emit(this.value));
          }
        });
        fromEvent(this.elementRef.nativeElement, 'mouseenter').pipe(takeUntil(this.destroy$)).subscribe(() => {
          if (!this.disabled) {
            this.ngZone.run(() => this.itemHover.emit(this.value));
          }
        });
      });
    }
    static {
      this.ɵfac = function NzOptionItemComponent_Factory(t) {
        return new (t || NzOptionItemComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i1$1.NzDestroyService));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzOptionItemComponent,
        selectors: [["nz-option-item"]],
        hostAttrs: [1, "ant-select-item", "ant-select-item-option"],
        hostVars: 9,
        hostBindings: function NzOptionItemComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵattribute("title", ctx.title);
            i0.ɵɵclassProp("ant-select-item-option-grouped", ctx.grouped)("ant-select-item-option-selected", ctx.selected && !ctx.disabled)("ant-select-item-option-disabled", ctx.disabled)("ant-select-item-option-active", ctx.activated && !ctx.disabled);
          }
        },
        inputs: {
          grouped: "grouped",
          customContent: "customContent",
          template: "template",
          disabled: "disabled",
          showState: "showState",
          title: "title",
          label: "label",
          value: "value",
          activatedValue: "activatedValue",
          listOfSelectedValue: "listOfSelectedValue",
          icon: "icon",
          compareWith: "compareWith"
        },
        outputs: {
          itemClick: "itemClick",
          itemHover: "itemHover"
        },
        standalone: true,
        features: [i0.ɵɵProvidersFeature([NzDestroyService]), i0.ɵɵNgOnChangesFeature, i0.ɵɵStandaloneFeature],
        decls: 5,
        vars: 3,
        consts: [[1, "ant-select-item-option-content"], [3, "ngIf", "ngIfElse"], ["noCustomContent", ""], ["class", "ant-select-item-option-state", "style", "user-select: none", "unselectable", "on", 4, "ngIf"], [3, "ngTemplateOutlet"], ["unselectable", "on", 1, "ant-select-item-option-state", 2, "user-select", "none"], ["nz-icon", "", "nzType", "check", "class", "ant-select-selected-icon", 4, "ngIf", "ngIfElse"], ["nz-icon", "", "nzType", "check", 1, "ant-select-selected-icon"]],
        template: function NzOptionItemComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 0);
            i0.ɵɵtemplate(1, NzOptionItemComponent_ng_template_1_Template, 1, 1, "ng-template", 1)(2, NzOptionItemComponent_ng_template_2_Template, 1, 1, "ng-template", null, 2, i0.ɵɵtemplateRefExtractor);
            i0.ɵɵelementEnd();
            i0.ɵɵtemplate(4, NzOptionItemComponent_div_4_Template, 2, 2, "div", 3);
          }
          if (rf & 2) {
            const _r2 = i0.ɵɵreference(3);
            i0.ɵɵadvance(1);
            i0.ɵɵproperty("ngIf", ctx.customContent)("ngIfElse", _r2);
            i0.ɵɵadvance(3);
            i0.ɵɵproperty("ngIf", ctx.showState && ctx.selected);
          }
        },
        dependencies: [NgIf, NgTemplateOutlet, NzIconModule, i1$2.NzIconDirective],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return NzOptionItemComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzOptionContainerComponent = /*#__PURE__*/(() => {
  class NzOptionContainerComponent {
    constructor() {
      this.notFoundContent = undefined;
      this.menuItemSelectedIcon = null;
      this.dropdownRender = null;
      this.activatedValue = null;
      this.listOfSelectedValue = [];
      this.mode = 'default';
      this.matchWidth = true;
      this.itemSize = 32;
      this.maxItemLength = 8;
      this.isMaxLimitReached = false;
      this.listOfContainerItem = [];
      this.itemClick = new EventEmitter();
      this.scrollToBottom = new EventEmitter();
      this.scrolledIndex = 0;
      this.ngZone = inject(NgZone);
      this.platformId = inject(PLATFORM_ID);
    }
    onItemClick(value) {
      this.itemClick.emit(value);
    }
    onItemHover(value) {
      // TODO: keydown.enter won't activate this value
      this.activatedValue = value;
    }
    trackValue(_index, option) {
      return option.key;
    }
    onScrolledIndexChange(index) {
      this.scrolledIndex = index;
      if (index === this.listOfContainerItem.length - this.maxItemLength - 1) {
        this.scrollToBottom.emit();
      }
    }
    scrollToActivatedValue() {
      const index = this.listOfContainerItem.findIndex(item => this.compareWith(item.key, this.activatedValue));
      if (index < this.scrolledIndex || index >= this.scrolledIndex + this.maxItemLength) {
        this.cdkVirtualScrollViewport.scrollToIndex(index || 0);
      }
    }
    ngOnChanges(changes) {
      const {
        listOfContainerItem,
        activatedValue
      } = changes;
      if (listOfContainerItem || activatedValue) {
        this.scrollToActivatedValue();
      }
    }
    ngAfterViewInit() {
      if (isPlatformBrowser(this.platformId)) {
        this.ngZone.runOutsideAngular(() => setTimeout(() => this.scrollToActivatedValue()));
      }
    }
    static {
      this.ɵfac = function NzOptionContainerComponent_Factory(t) {
        return new (t || NzOptionContainerComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzOptionContainerComponent,
        selectors: [["nz-option-container"]],
        viewQuery: function NzOptionContainerComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(CdkVirtualScrollViewport, 7);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.cdkVirtualScrollViewport = _t.first);
          }
        },
        hostAttrs: [1, "ant-select-dropdown"],
        inputs: {
          notFoundContent: "notFoundContent",
          menuItemSelectedIcon: "menuItemSelectedIcon",
          dropdownRender: "dropdownRender",
          activatedValue: "activatedValue",
          listOfSelectedValue: "listOfSelectedValue",
          compareWith: "compareWith",
          mode: "mode",
          matchWidth: "matchWidth",
          itemSize: "itemSize",
          maxItemLength: "maxItemLength",
          isMaxLimitReached: "isMaxLimitReached",
          listOfContainerItem: "listOfContainerItem"
        },
        outputs: {
          itemClick: "itemClick",
          scrollToBottom: "scrollToBottom"
        },
        exportAs: ["nzOptionContainer"],
        standalone: true,
        features: [i0.ɵɵNgOnChangesFeature, i0.ɵɵStandaloneFeature],
        decls: 5,
        vars: 14,
        consts: [["class", "ant-select-item-empty", 4, "ngIf"], [3, "itemSize", "maxBufferPx", "minBufferPx", "scrolledIndexChange"], ["cdkVirtualFor", "", 3, "cdkVirtualForOf", "cdkVirtualForTrackBy", "cdkVirtualForTemplateCacheSize"], [3, "ngTemplateOutlet"], [1, "ant-select-item-empty"], ["nzComponentName", "select", 3, "specificContent"], [3, "ngSwitch"], [3, "nzLabel", 4, "ngSwitchCase"], [3, "icon", "customContent", "template", "grouped", "disabled", "showState", "title", "label", "compareWith", "activatedValue", "listOfSelectedValue", "value", "itemHover", "itemClick", 4, "ngSwitchCase"], [3, "nzLabel"], [3, "icon", "customContent", "template", "grouped", "disabled", "showState", "title", "label", "compareWith", "activatedValue", "listOfSelectedValue", "value", "itemHover", "itemClick"]],
        template: function NzOptionContainerComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "div");
            i0.ɵɵtemplate(1, NzOptionContainerComponent_div_1_Template, 2, 1, "div", 0);
            i0.ɵɵelementStart(2, "cdk-virtual-scroll-viewport", 1);
            i0.ɵɵlistener("scrolledIndexChange", function NzOptionContainerComponent_Template_cdk_virtual_scroll_viewport_scrolledIndexChange_2_listener($event) {
              return ctx.onScrolledIndexChange($event);
            });
            i0.ɵɵtemplate(3, NzOptionContainerComponent_ng_template_3_Template, 3, 3, "ng-template", 2);
            i0.ɵɵelementEnd();
            i0.ɵɵtemplate(4, NzOptionContainerComponent_ng_template_4_Template, 0, 0, "ng-template", 3);
            i0.ɵɵelementEnd();
          }
          if (rf & 2) {
            i0.ɵɵadvance(1);
            i0.ɵɵproperty("ngIf", ctx.listOfContainerItem.length === 0);
            i0.ɵɵadvance(1);
            i0.ɵɵstyleProp("height", ctx.listOfContainerItem.length * ctx.itemSize, "px")("max-height", ctx.itemSize * ctx.maxItemLength, "px");
            i0.ɵɵclassProp("full-width", !ctx.matchWidth);
            i0.ɵɵproperty("itemSize", ctx.itemSize)("maxBufferPx", ctx.itemSize * ctx.maxItemLength)("minBufferPx", ctx.itemSize * ctx.maxItemLength);
            i0.ɵɵadvance(1);
            i0.ɵɵproperty("cdkVirtualForOf", ctx.listOfContainerItem)("cdkVirtualForTrackBy", ctx.trackValue)("cdkVirtualForTemplateCacheSize", 0);
            i0.ɵɵadvance(1);
            i0.ɵɵproperty("ngTemplateOutlet", ctx.dropdownRender);
          }
        },
        dependencies: [NzEmptyModule, i1$3.NzEmbedEmptyComponent, NgIf, NgSwitch, NzOptionItemGroupComponent, NgSwitchCase, NzOptionItemComponent, NgTemplateOutlet, OverlayModule, i2.CdkFixedSizeVirtualScroll, i2.CdkVirtualForOf, i2.CdkVirtualScrollViewport, NzOverlayModule],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return NzOptionContainerComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NzOptionComponent = /*#__PURE__*/(() => {
  class NzOptionComponent {
    constructor(nzOptionGroupComponent, destroy$) {
      this.nzOptionGroupComponent = nzOptionGroupComponent;
      this.destroy$ = destroy$;
      this.changes = new Subject();
      this.groupLabel = null;
      this.nzLabel = null;
      this.nzValue = null;
      this.nzDisabled = false;
      this.nzHide = false;
      this.nzCustomContent = false;
    }
    ngOnInit() {
      if (this.nzOptionGroupComponent) {
        this.nzOptionGroupComponent.changes.pipe(startWith(true), takeUntil(this.destroy$)).subscribe(() => {
          this.groupLabel = this.nzOptionGroupComponent.nzLabel;
        });
      }
    }
    ngOnChanges() {
      this.changes.next();
    }
    static {
      this.ɵfac = function NzOptionComponent_Factory(t) {
        return new (t || NzOptionComponent)(i0.ɵɵdirectiveInject(NzOptionGroupComponent, 8), i0.ɵɵdirectiveInject(i1$1.NzDestroyService));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzOptionComponent,
        selectors: [["nz-option"]],
        viewQuery: function NzOptionComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(TemplateRef, 7);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.template = _t.first);
          }
        },
        inputs: {
          nzTitle: "nzTitle",
          nzLabel: "nzLabel",
          nzValue: "nzValue",
          nzKey: "nzKey",
          nzDisabled: "nzDisabled",
          nzHide: "nzHide",
          nzCustomContent: "nzCustomContent"
        },
        exportAs: ["nzOption"],
        standalone: true,
        features: [i0.ɵɵProvidersFeature([NzDestroyService]), i0.ɵɵNgOnChangesFeature, i0.ɵɵStandaloneFeature],
        ngContentSelectors: _c0,
        decls: 1,
        vars: 0,
        template: function NzOptionComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵtemplate(0, NzOptionComponent_ng_template_0_Template, 1, 0, "ng-template");
          }
        },
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  __decorate([InputBoolean()], NzOptionComponent.prototype, "nzDisabled", void 0);
  __decorate([InputBoolean()], NzOptionComponent.prototype, "nzHide", void 0);
  __decorate([InputBoolean()], NzOptionComponent.prototype, "nzCustomContent", void 0);
  return NzOptionComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzSelectArrowComponent = /*#__PURE__*/(() => {
  class NzSelectArrowComponent {
    constructor() {
      this.listOfValue = [];
      this.loading = false;
      this.search = false;
      this.showArrow = false;
      this.isMaxTagCountSet = false;
      this.suffixIcon = null;
      this.feedbackIcon = null;
      this.nzMaxMultipleCount = Infinity;
    }
    static {
      this.ɵfac = function NzSelectArrowComponent_Factory(t) {
        return new (t || NzSelectArrowComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzSelectArrowComponent,
        selectors: [["nz-select-arrow"]],
        hostAttrs: [1, "ant-select-arrow"],
        hostVars: 2,
        hostBindings: function NzSelectArrowComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵclassProp("ant-select-arrow-loading", ctx.loading);
          }
        },
        inputs: {
          listOfValue: "listOfValue",
          loading: "loading",
          search: "search",
          showArrow: "showArrow",
          isMaxTagCountSet: "isMaxTagCountSet",
          suffixIcon: "suffixIcon",
          feedbackIcon: "feedbackIcon",
          nzMaxMultipleCount: "nzMaxMultipleCount"
        },
        standalone: true,
        features: [i0.ɵɵStandaloneFeature],
        decls: 5,
        vars: 4,
        consts: [[4, "ngIf"], ["nz-icon", "", "nzType", "loading", 4, "ngIf", "ngIfElse"], ["defaultArrow", ""], [4, "nzStringTemplateOutlet"], ["nz-icon", "", "nzType", "loading"], [4, "ngIf", "ngIfElse"], ["suffixTemplate", ""], ["nz-icon", "", "nzType", "down", 4, "ngIf"], ["nz-icon", "", "nzType", "search", 4, "ngIf"], ["nz-icon", "", "nzType", "down"], ["nz-icon", "", "nzType", "search"], ["nz-icon", "", 3, "nzType", 4, "ngIf"], ["nz-icon", "", 3, "nzType"]],
        template: function NzSelectArrowComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵtemplate(0, NzSelectArrowComponent_ng_container_0_Template, 3, 2, "ng-container", 0)(1, NzSelectArrowComponent_span_1_Template, 1, 0, "span", 1)(2, NzSelectArrowComponent_ng_template_2_Template, 3, 2, "ng-template", null, 2, i0.ɵɵtemplateRefExtractor)(4, NzSelectArrowComponent_ng_container_4_Template, 2, 1, "ng-container", 3);
          }
          if (rf & 2) {
            const _r3 = i0.ɵɵreference(3);
            i0.ɵɵproperty("ngIf", ctx.isMaxTagCountSet);
            i0.ɵɵadvance(1);
            i0.ɵɵproperty("ngIf", ctx.loading)("ngIfElse", _r3);
            i0.ɵɵadvance(3);
            i0.ɵɵproperty("nzStringTemplateOutlet", ctx.feedbackIcon);
          }
        },
        dependencies: [NzIconModule, i1$2.NzIconDirective, NgIf, NzOutletModule, i1.NzStringTemplateOutletDirective],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return NzSelectArrowComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzSelectClearComponent = /*#__PURE__*/(() => {
  class NzSelectClearComponent {
    constructor() {
      this.clearIcon = null;
      this.clear = new EventEmitter();
    }
    onClick(e) {
      e.preventDefault();
      e.stopPropagation();
      this.clear.emit(e);
    }
    static {
      this.ɵfac = function NzSelectClearComponent_Factory(t) {
        return new (t || NzSelectClearComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzSelectClearComponent,
        selectors: [["nz-select-clear"]],
        hostAttrs: [1, "ant-select-clear"],
        hostBindings: function NzSelectClearComponent_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("click", function NzSelectClearComponent_click_HostBindingHandler($event) {
              return ctx.onClick($event);
            });
          }
        },
        inputs: {
          clearIcon: "clearIcon"
        },
        outputs: {
          clear: "clear"
        },
        standalone: true,
        features: [i0.ɵɵStandaloneFeature],
        decls: 1,
        vars: 2,
        consts: [["nz-icon", "", "nzType", "close-circle", "nzTheme", "fill", "class", "ant-select-close-icon", 4, "ngIf", "ngIfElse"], ["nz-icon", "", "nzType", "close-circle", "nzTheme", "fill", 1, "ant-select-close-icon"]],
        template: function NzSelectClearComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵtemplate(0, NzSelectClearComponent_span_0_Template, 1, 0, "span", 0);
          }
          if (rf & 2) {
            i0.ɵɵproperty("ngIf", !ctx.clearIcon)("ngIfElse", ctx.clearIcon);
          }
        },
        dependencies: [NzIconModule, i1$2.NzIconDirective, NgIf],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return NzSelectClearComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzSelectItemComponent = /*#__PURE__*/(() => {
  class NzSelectItemComponent {
    constructor() {
      this.disabled = false;
      this.label = null;
      this.deletable = false;
      this.removeIcon = null;
      this.contentTemplateOutletContext = null;
      this.contentTemplateOutlet = null;
      this.delete = new EventEmitter();
    }
    onDelete(e) {
      e.preventDefault();
      e.stopPropagation();
      if (!this.disabled) {
        this.delete.next(e);
      }
    }
    static {
      this.ɵfac = function NzSelectItemComponent_Factory(t) {
        return new (t || NzSelectItemComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzSelectItemComponent,
        selectors: [["nz-select-item"]],
        hostAttrs: [1, "ant-select-selection-item"],
        hostVars: 3,
        hostBindings: function NzSelectItemComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵattribute("title", ctx.label);
            i0.ɵɵclassProp("ant-select-selection-item-disabled", ctx.disabled);
          }
        },
        inputs: {
          disabled: "disabled",
          label: "label",
          deletable: "deletable",
          removeIcon: "removeIcon",
          contentTemplateOutletContext: "contentTemplateOutletContext",
          contentTemplateOutlet: "contentTemplateOutlet"
        },
        outputs: {
          delete: "delete"
        },
        standalone: true,
        features: [i0.ɵɵStandaloneFeature],
        decls: 2,
        vars: 5,
        consts: [[4, "nzStringTemplateOutlet", "nzStringTemplateOutletContext"], ["class", "ant-select-selection-item-remove", 3, "click", 4, "ngIf"], ["class", "ant-select-selection-item-content", 4, "ngIf", "ngIfElse"], ["labelTemplate", ""], [1, "ant-select-selection-item-content"], [1, "ant-select-selection-item-remove", 3, "click"], ["nz-icon", "", "nzType", "close", 4, "ngIf", "ngIfElse"], ["nz-icon", "", "nzType", "close"]],
        template: function NzSelectItemComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵtemplate(0, NzSelectItemComponent_ng_container_0_Template, 4, 2, "ng-container", 0)(1, NzSelectItemComponent_span_1_Template, 2, 2, "span", 1);
          }
          if (rf & 2) {
            i0.ɵɵproperty("nzStringTemplateOutlet", ctx.contentTemplateOutlet)("nzStringTemplateOutletContext", i0.ɵɵpureFunction1(3, _c1, ctx.contentTemplateOutletContext));
            i0.ɵɵadvance(1);
            i0.ɵɵproperty("ngIf", ctx.deletable && !ctx.disabled);
          }
        },
        dependencies: [NzOutletModule, i1.NzStringTemplateOutletDirective, NgIf, NzIconModule, i1$2.NzIconDirective],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return NzSelectItemComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzSelectPlaceholderComponent = /*#__PURE__*/(() => {
  class NzSelectPlaceholderComponent {
    constructor() {
      this.placeholder = null;
    }
    static {
      this.ɵfac = function NzSelectPlaceholderComponent_Factory(t) {
        return new (t || NzSelectPlaceholderComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzSelectPlaceholderComponent,
        selectors: [["nz-select-placeholder"]],
        hostAttrs: [1, "ant-select-selection-placeholder"],
        inputs: {
          placeholder: "placeholder"
        },
        standalone: true,
        features: [i0.ɵɵStandaloneFeature],
        decls: 1,
        vars: 1,
        consts: [[4, "nzStringTemplateOutlet"]],
        template: function NzSelectPlaceholderComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵtemplate(0, NzSelectPlaceholderComponent_ng_container_0_Template, 2, 1, "ng-container", 0);
          }
          if (rf & 2) {
            i0.ɵɵproperty("nzStringTemplateOutlet", ctx.placeholder);
          }
        },
        dependencies: [NzOutletModule, i1.NzStringTemplateOutletDirective],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return NzSelectPlaceholderComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NzSelectSearchComponent = /*#__PURE__*/(() => {
  class NzSelectSearchComponent {
    setCompositionState(isComposing) {
      this.isComposingChange.next(isComposing);
    }
    onValueChange(value) {
      this.value = value;
      this.valueChange.next(value);
      if (this.mirrorSync) {
        this.syncMirrorWidth();
      }
    }
    clearInputValue() {
      const inputDOM = this.inputElement.nativeElement;
      inputDOM.value = '';
      this.onValueChange('');
    }
    syncMirrorWidth() {
      const mirrorDOM = this.mirrorElement.nativeElement;
      const hostDOM = this.elementRef.nativeElement;
      const inputDOM = this.inputElement.nativeElement;
      this.renderer.removeStyle(hostDOM, 'width');
      this.renderer.setProperty(mirrorDOM, 'textContent', `${inputDOM.value}\u00a0`);
      this.renderer.setStyle(hostDOM, 'width', `${mirrorDOM.scrollWidth}px`);
    }
    focus() {
      this.focusMonitor.focusVia(this.inputElement, 'keyboard');
    }
    blur() {
      this.inputElement.nativeElement.blur();
    }
    constructor(elementRef, renderer, focusMonitor) {
      this.elementRef = elementRef;
      this.renderer = renderer;
      this.focusMonitor = focusMonitor;
      this.nzId = null;
      this.disabled = false;
      this.mirrorSync = false;
      this.showInput = true;
      this.focusTrigger = false;
      this.value = '';
      this.autofocus = false;
      this.valueChange = new EventEmitter();
      this.isComposingChange = new EventEmitter();
    }
    ngOnChanges(changes) {
      const inputDOM = this.inputElement.nativeElement;
      const {
        focusTrigger,
        showInput
      } = changes;
      if (showInput) {
        if (this.showInput) {
          this.renderer.removeAttribute(inputDOM, 'readonly');
        } else {
          this.renderer.setAttribute(inputDOM, 'readonly', 'readonly');
        }
      }
      // IE11 cannot input value if focused before removing readonly
      if (focusTrigger && focusTrigger.currentValue === true && focusTrigger.previousValue === false) {
        inputDOM.focus();
      }
    }
    ngAfterViewInit() {
      if (this.mirrorSync) {
        this.syncMirrorWidth();
      }
      if (this.autofocus) {
        this.focus();
      }
    }
    static {
      this.ɵfac = function NzSelectSearchComponent_Factory(t) {
        return new (t || NzSelectSearchComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i1$4.FocusMonitor));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzSelectSearchComponent,
        selectors: [["nz-select-search"]],
        viewQuery: function NzSelectSearchComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(_c2, 7);
            i0.ɵɵviewQuery(_c3, 5);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.inputElement = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.mirrorElement = _t.first);
          }
        },
        hostAttrs: [1, "ant-select-selection-search"],
        inputs: {
          nzId: "nzId",
          disabled: "disabled",
          mirrorSync: "mirrorSync",
          showInput: "showInput",
          focusTrigger: "focusTrigger",
          value: "value",
          autofocus: "autofocus"
        },
        outputs: {
          valueChange: "valueChange",
          isComposingChange: "isComposingChange"
        },
        standalone: true,
        features: [i0.ɵɵProvidersFeature([{
          provide: COMPOSITION_BUFFER_MODE,
          useValue: false
        }]), i0.ɵɵNgOnChangesFeature, i0.ɵɵStandaloneFeature],
        decls: 3,
        vars: 7,
        consts: [["autocomplete", "off", 1, "ant-select-selection-search-input", 3, "ngModel", "disabled", "ngModelChange", "compositionstart", "compositionend"], ["inputElement", ""], ["class", "ant-select-selection-search-mirror", 4, "ngIf"], [1, "ant-select-selection-search-mirror"], ["mirrorElement", ""]],
        template: function NzSelectSearchComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "input", 0, 1);
            i0.ɵɵlistener("ngModelChange", function NzSelectSearchComponent_Template_input_ngModelChange_0_listener($event) {
              return ctx.onValueChange($event);
            })("compositionstart", function NzSelectSearchComponent_Template_input_compositionstart_0_listener() {
              return ctx.setCompositionState(true);
            })("compositionend", function NzSelectSearchComponent_Template_input_compositionend_0_listener() {
              return ctx.setCompositionState(false);
            });
            i0.ɵɵelementEnd();
            i0.ɵɵtemplate(2, NzSelectSearchComponent_span_2_Template, 2, 0, "span", 2);
          }
          if (rf & 2) {
            i0.ɵɵstyleProp("opacity", ctx.showInput ? null : 0);
            i0.ɵɵproperty("ngModel", ctx.value)("disabled", ctx.disabled);
            i0.ɵɵattribute("id", ctx.nzId)("autofocus", ctx.autofocus ? "autofocus" : null);
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("ngIf", ctx.mirrorSync);
          }
        },
        dependencies: [FormsModule, i2$1.DefaultValueAccessor, i2$1.NgControlStatus, i2$1.NgModel, NgIf],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return NzSelectSearchComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzSelectTopControlComponent = /*#__PURE__*/(() => {
  class NzSelectTopControlComponent {
    updateTemplateVariable() {
      const isSelectedValueEmpty = this.listOfTopItem.length === 0;
      this.isShowPlaceholder = isSelectedValueEmpty && !this.isComposing && !this.inputValue;
      this.isShowSingleLabel = !isSelectedValueEmpty && !this.isComposing && !this.inputValue;
    }
    isComposingChange(isComposing) {
      this.isComposing = isComposing;
      this.updateTemplateVariable();
    }
    onInputValueChange(value) {
      if (value !== this.inputValue) {
        this.inputValue = value;
        this.updateTemplateVariable();
        this.inputValueChange.emit(value);
        this.tokenSeparate(value, this.tokenSeparators);
      }
    }
    tokenSeparate(inputValue, tokenSeparators) {
      const includesSeparators = (str, separators) => {
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < separators.length; ++i) {
          if (str.lastIndexOf(separators[i]) > 0) {
            return true;
          }
        }
        return false;
      };
      const splitBySeparators = (str, separators) => {
        const reg = new RegExp(`[${separators.join()}]`);
        const array = str.split(reg).filter(token => token);
        return [...new Set(array)];
      };
      if (inputValue && inputValue.length && tokenSeparators.length && this.mode !== 'default' && includesSeparators(inputValue, tokenSeparators)) {
        const listOfLabel = splitBySeparators(inputValue, tokenSeparators);
        this.tokenize.next(listOfLabel);
      }
    }
    clearInputValue() {
      if (this.nzSelectSearchComponent) {
        this.nzSelectSearchComponent.clearInputValue();
      }
    }
    focus() {
      if (this.nzSelectSearchComponent) {
        this.nzSelectSearchComponent.focus();
      }
    }
    blur() {
      if (this.nzSelectSearchComponent) {
        this.nzSelectSearchComponent.blur();
      }
    }
    trackValue(_index, option) {
      return option.nzValue;
    }
    onDeleteItem(item) {
      if (!this.disabled && !item.nzDisabled) {
        this.deleteItem.next(item);
      }
    }
    constructor(elementRef, ngZone, noAnimation) {
      this.elementRef = elementRef;
      this.ngZone = ngZone;
      this.noAnimation = noAnimation;
      this.nzId = null;
      this.showSearch = false;
      this.placeHolder = null;
      this.open = false;
      this.maxTagCount = Infinity;
      this.autofocus = false;
      this.disabled = false;
      this.mode = 'default';
      this.customTemplate = null;
      this.maxTagPlaceholder = null;
      this.removeIcon = null;
      this.listOfTopItem = [];
      this.tokenSeparators = [];
      this.tokenize = new EventEmitter();
      this.inputValueChange = new EventEmitter();
      this.deleteItem = new EventEmitter();
      this.listOfSlicedItem = [];
      this.isShowPlaceholder = true;
      this.isShowSingleLabel = false;
      this.isComposing = false;
      this.inputValue = null;
      this.destroy$ = new Subject();
    }
    ngOnChanges(changes) {
      const {
        listOfTopItem,
        maxTagCount,
        customTemplate,
        maxTagPlaceholder
      } = changes;
      if (listOfTopItem) {
        this.updateTemplateVariable();
      }
      if (listOfTopItem || maxTagCount || customTemplate || maxTagPlaceholder) {
        const listOfSlicedItem = this.listOfTopItem.slice(0, this.maxTagCount).map(o => ({
          nzLabel: o.nzLabel,
          nzValue: o.nzValue,
          nzDisabled: o.nzDisabled,
          contentTemplateOutlet: this.customTemplate,
          contentTemplateOutletContext: o
        }));
        if (this.listOfTopItem.length > this.maxTagCount) {
          const exceededLabel = `+ ${this.listOfTopItem.length - this.maxTagCount} ...`;
          const listOfSelectedValue = this.listOfTopItem.map(item => item.nzValue);
          const exceededItem = {
            nzLabel: exceededLabel,
            nzValue: '$$__nz_exceeded_item',
            nzDisabled: true,
            contentTemplateOutlet: this.maxTagPlaceholder,
            contentTemplateOutletContext: listOfSelectedValue.slice(this.maxTagCount)
          };
          listOfSlicedItem.push(exceededItem);
        }
        this.listOfSlicedItem = listOfSlicedItem;
      }
    }
    ngOnInit() {
      this.ngZone.runOutsideAngular(() => {
        fromEvent(this.elementRef.nativeElement, 'click').pipe(takeUntil(this.destroy$)).subscribe(event => {
          // `HTMLElement.focus()` is a native DOM API which doesn't require Angular to run change detection.
          if (event.target !== this.nzSelectSearchComponent.inputElement.nativeElement) {
            this.nzSelectSearchComponent.focus();
          }
        });
        fromEvent(this.elementRef.nativeElement, 'keydown').pipe(takeUntil(this.destroy$)).subscribe(event => {
          if (event.target instanceof HTMLInputElement) {
            const inputValue = event.target.value;
            if (event.keyCode === BACKSPACE && this.mode !== 'default' && !inputValue && this.listOfTopItem.length > 0) {
              event.preventDefault();
              // Run change detection only if the user has pressed the `Backspace` key and the following condition is met.
              this.ngZone.run(() => this.onDeleteItem(this.listOfTopItem[this.listOfTopItem.length - 1]));
            }
          }
        });
      });
    }
    ngOnDestroy() {
      this.destroy$.next();
    }
    static {
      this.ɵfac = function NzSelectTopControlComponent_Factory(t) {
        return new (t || NzSelectTopControlComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i1$5.NzNoAnimationDirective, 9));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzSelectTopControlComponent,
        selectors: [["nz-select-top-control"]],
        viewQuery: function NzSelectTopControlComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(NzSelectSearchComponent, 5);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.nzSelectSearchComponent = _t.first);
          }
        },
        hostAttrs: [1, "ant-select-selector"],
        inputs: {
          nzId: "nzId",
          showSearch: "showSearch",
          placeHolder: "placeHolder",
          open: "open",
          maxTagCount: "maxTagCount",
          autofocus: "autofocus",
          disabled: "disabled",
          mode: "mode",
          customTemplate: "customTemplate",
          maxTagPlaceholder: "maxTagPlaceholder",
          removeIcon: "removeIcon",
          listOfTopItem: "listOfTopItem",
          tokenSeparators: "tokenSeparators"
        },
        outputs: {
          tokenize: "tokenize",
          inputValueChange: "inputValueChange",
          deleteItem: "deleteItem"
        },
        exportAs: ["nzSelectTopControl"],
        standalone: true,
        features: [i0.ɵɵNgOnChangesFeature, i0.ɵɵStandaloneFeature],
        decls: 4,
        vars: 3,
        consts: [[3, "ngSwitch"], [4, "ngSwitchCase"], [4, "ngSwitchDefault"], [3, "placeholder", 4, "ngIf"], [3, "nzId", "disabled", "value", "showInput", "mirrorSync", "autofocus", "focusTrigger", "isComposingChange", "valueChange"], [3, "deletable", "disabled", "removeIcon", "label", "contentTemplateOutlet", "contentTemplateOutletContext", 4, "ngIf"], [3, "deletable", "disabled", "removeIcon", "label", "contentTemplateOutlet", "contentTemplateOutletContext"], [3, "removeIcon", "label", "disabled", "contentTemplateOutlet", "deletable", "contentTemplateOutletContext", "delete", 4, "ngFor", "ngForOf", "ngForTrackBy"], [3, "nzId", "disabled", "value", "autofocus", "showInput", "mirrorSync", "focusTrigger", "isComposingChange", "valueChange"], [3, "removeIcon", "label", "disabled", "contentTemplateOutlet", "deletable", "contentTemplateOutletContext", "delete"], [3, "placeholder"]],
        template: function NzSelectTopControlComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementContainerStart(0, 0);
            i0.ɵɵtemplate(1, NzSelectTopControlComponent_ng_container_1_Template, 3, 8, "ng-container", 1)(2, NzSelectTopControlComponent_ng_container_2_Template, 3, 9, "ng-container", 2);
            i0.ɵɵelementContainerEnd();
            i0.ɵɵtemplate(3, NzSelectTopControlComponent_nz_select_placeholder_3_Template, 1, 1, "nz-select-placeholder", 3);
          }
          if (rf & 2) {
            i0.ɵɵproperty("ngSwitch", ctx.mode);
            i0.ɵɵadvance(1);
            i0.ɵɵproperty("ngSwitchCase", "default");
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("ngIf", ctx.isShowPlaceholder);
          }
        },
        dependencies: [NgSwitch, NzSelectSearchComponent, NgSwitchCase, NzSelectItemComponent, NgIf, NgSwitchDefault, NgFor, NzSelectPlaceholderComponent],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return NzSelectTopControlComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const defaultFilterOption = (searchValue, item) => {
  if (item && item.nzLabel) {
    return item.nzLabel.toString().toLowerCase().indexOf(searchValue.toLowerCase()) > -1;
  } else {
    return false;
  }
};
const NZ_CONFIG_MODULE_NAME = 'select';
let NzSelectComponent = /*#__PURE__*/(() => {
  class NzSelectComponent {
    set nzShowArrow(value) {
      this._nzShowArrow = value;
    }
    get nzShowArrow() {
      return this._nzShowArrow === undefined ? this.nzMode === 'default' : this._nzShowArrow;
    }
    get isMaxTagCountSet() {
      return this.nzMaxMultipleCount !== Infinity;
    }
    generateTagItem(value) {
      return {
        nzValue: value,
        nzLabel: value,
        type: 'item'
      };
    }
    onItemClick(value) {
      this.activatedValue = value;
      if (this.nzMode === 'default') {
        if (this.listOfValue.length === 0 || !this.compareWith(this.listOfValue[0], value)) {
          this.updateListOfValue([value]);
        }
        this.setOpenState(false);
      } else {
        const targetIndex = this.listOfValue.findIndex(o => this.compareWith(o, value));
        if (targetIndex !== -1) {
          const listOfValueAfterRemoved = this.listOfValue.filter((_, i) => i !== targetIndex);
          this.updateListOfValue(listOfValueAfterRemoved);
        } else if (this.listOfValue.length < this.nzMaxMultipleCount) {
          const listOfValueAfterAdded = [...this.listOfValue, value];
          this.updateListOfValue(listOfValueAfterAdded);
        }
        this.focus();
        if (this.nzAutoClearSearchValue) {
          this.clearInput();
        }
      }
    }
    onItemDelete(item) {
      const listOfSelectedValue = this.listOfValue.filter(v => !this.compareWith(v, item.nzValue));
      this.updateListOfValue(listOfSelectedValue);
      this.clearInput();
    }
    updateListOfContainerItem() {
      let listOfContainerItem = this.listOfTagAndTemplateItem.filter(item => !item.nzHide).filter(item => {
        if (!this.nzServerSearch && this.searchValue) {
          return this.nzFilterOption(this.searchValue, item);
        } else {
          return true;
        }
      });
      if (this.nzMode === 'tags' && this.searchValue) {
        const matchedItem = this.listOfTagAndTemplateItem.find(item => item.nzLabel === this.searchValue);
        if (!matchedItem) {
          const tagItem = this.generateTagItem(this.searchValue);
          listOfContainerItem = [tagItem, ...listOfContainerItem];
          this.activatedValue = tagItem.nzValue;
        } else {
          this.activatedValue = matchedItem.nzValue;
        }
      }
      const activatedItem = listOfContainerItem.find(item => item.nzLabel === this.searchValue) || listOfContainerItem.find(item => this.compareWith(item.nzValue, this.activatedValue)) || listOfContainerItem.find(item => this.compareWith(item.nzValue, this.listOfValue[0])) || listOfContainerItem[0];
      this.activatedValue = activatedItem && activatedItem.nzValue || null;
      let listOfGroupLabel = [];
      if (this.isReactiveDriven) {
        listOfGroupLabel = [...new Set(this.nzOptions.filter(o => o.groupLabel).map(o => o.groupLabel))];
      } else {
        if (this.listOfNzOptionGroupComponent) {
          listOfGroupLabel = this.listOfNzOptionGroupComponent.map(o => o.nzLabel);
        }
      }
      /** insert group item **/
      listOfGroupLabel.forEach(label => {
        const index = listOfContainerItem.findIndex(item => label === item.groupLabel);
        if (index > -1) {
          const groupItem = {
            groupLabel: label,
            type: 'group',
            key: label
          };
          listOfContainerItem.splice(index, 0, groupItem);
        }
      });
      this.listOfContainerItem = [...listOfContainerItem];
      this.updateCdkConnectedOverlayPositions();
    }
    clearInput() {
      this.nzSelectTopControlComponent.clearInputValue();
    }
    updateListOfValue(listOfValue) {
      const covertListToModel = (list, mode) => {
        if (mode === 'default') {
          if (list.length > 0) {
            return list[0];
          } else {
            return null;
          }
        } else {
          return list;
        }
      };
      const model = covertListToModel(listOfValue, this.nzMode);
      if (this.value !== model) {
        this.listOfValue = listOfValue;
        this.listOfValue$.next(listOfValue);
        this.value = model;
        this.onChange(this.value);
      }
      this.isMaxLimitReached = this.nzMaxMultipleCount !== Infinity && this.listOfValue.length === this.nzMaxMultipleCount;
    }
    onTokenSeparate(listOfLabel) {
      const listOfMatchedValue = this.listOfTagAndTemplateItem.filter(item => listOfLabel.findIndex(label => label === item.nzLabel) !== -1).map(item => item.nzValue).filter(item => this.listOfValue.findIndex(v => this.compareWith(v, item)) === -1);
      if (this.nzMode === 'multiple') {
        this.updateListOfValue([...this.listOfValue, ...listOfMatchedValue]);
      } else if (this.nzMode === 'tags') {
        const listOfUnMatchedLabel = listOfLabel.filter(label => this.listOfTagAndTemplateItem.findIndex(item => item.nzLabel === label) === -1);
        this.updateListOfValue([...this.listOfValue, ...listOfMatchedValue, ...listOfUnMatchedLabel]);
      }
      this.clearInput();
    }
    onKeyDown(e) {
      if (this.nzDisabled) {
        return;
      }
      const listOfFilteredOptionNotDisabled = this.listOfContainerItem.filter(item => item.type === 'item').filter(item => !item.nzDisabled);
      const activatedIndex = listOfFilteredOptionNotDisabled.findIndex(item => this.compareWith(item.nzValue, this.activatedValue));
      switch (e.keyCode) {
        case UP_ARROW:
          e.preventDefault();
          if (this.nzOpen && listOfFilteredOptionNotDisabled.length > 0) {
            const preIndex = activatedIndex > 0 ? activatedIndex - 1 : listOfFilteredOptionNotDisabled.length - 1;
            this.activatedValue = listOfFilteredOptionNotDisabled[preIndex].nzValue;
          }
          break;
        case DOWN_ARROW:
          e.preventDefault();
          if (this.nzOpen && listOfFilteredOptionNotDisabled.length > 0) {
            const nextIndex = activatedIndex < listOfFilteredOptionNotDisabled.length - 1 ? activatedIndex + 1 : 0;
            this.activatedValue = listOfFilteredOptionNotDisabled[nextIndex].nzValue;
          } else {
            this.setOpenState(true);
          }
          break;
        case ENTER:
          e.preventDefault();
          if (this.nzOpen) {
            if (isNotNil(this.activatedValue) && activatedIndex !== -1) {
              this.onItemClick(this.activatedValue);
            }
          } else {
            this.setOpenState(true);
          }
          break;
        case SPACE:
          if (!this.nzOpen) {
            this.setOpenState(true);
            e.preventDefault();
          }
          break;
        case TAB:
          if (this.nzSelectOnTab) {
            if (this.nzOpen) {
              e.preventDefault();
              if (isNotNil(this.activatedValue)) {
                this.onItemClick(this.activatedValue);
              }
            }
          } else {
            this.setOpenState(false);
          }
          break;
        case ESCAPE:
          /**
           * Skip the ESCAPE processing, it will be handled in {@link onOverlayKeyDown}.
           */
          break;
        default:
          if (!this.nzOpen) {
            this.setOpenState(true);
          }
      }
    }
    setOpenState(value) {
      if (this.nzOpen !== value) {
        this.nzOpen = value;
        this.nzOpenChange.emit(value);
        this.onOpenChange();
        this.cdr.markForCheck();
      }
    }
    onOpenChange() {
      this.updateCdkConnectedOverlayStatus();
      if (this.nzAutoClearSearchValue) {
        this.clearInput();
      }
    }
    onInputValueChange(value) {
      this.searchValue = value;
      this.updateListOfContainerItem();
      this.nzOnSearch.emit(value);
      this.updateCdkConnectedOverlayPositions();
    }
    onClearSelection() {
      this.updateListOfValue([]);
    }
    onClickOutside(event) {
      if (!this.host.nativeElement.contains(event.target)) {
        this.setOpenState(false);
      }
    }
    focus() {
      this.nzSelectTopControlComponent.focus();
    }
    blur() {
      this.nzSelectTopControlComponent.blur();
    }
    onPositionChange(position) {
      const placement = getPlacementName(position);
      this.dropDownPosition = placement;
    }
    updateCdkConnectedOverlayStatus() {
      if (this.platform.isBrowser && this.originElement.nativeElement) {
        const triggerWidth = this.triggerWidth;
        cancelRequestAnimationFrame(this.requestId);
        this.requestId = reqAnimFrame(() => {
          // Blink triggers style and layout pipelines anytime the `getBoundingClientRect()` is called, which may cause a
          // frame drop. That's why it's scheduled through the `requestAnimationFrame` to unload the composite thread.
          this.triggerWidth = this.originElement.nativeElement.getBoundingClientRect().width;
          if (triggerWidth !== this.triggerWidth) {
            // The `requestAnimationFrame` will trigger change detection, but we're inside an `OnPush` component which won't have
            // the `ChecksEnabled` state. Calling `markForCheck()` will allow Angular to run the change detection from the root component
            // down to the `nz-select`. But we'll trigger only local change detection if the `triggerWidth` has been changed.
            this.cdr.detectChanges();
          }
        });
      }
    }
    updateCdkConnectedOverlayPositions() {
      reqAnimFrame(() => {
        this.cdkConnectedOverlay?.overlayRef?.updatePosition();
      });
    }
    constructor(ngZone, destroy$, nzConfigService, cdr, host, renderer, platform, focusMonitor, directionality, noAnimation, nzFormStatusService, nzFormNoStatusService) {
      this.ngZone = ngZone;
      this.destroy$ = destroy$;
      this.nzConfigService = nzConfigService;
      this.cdr = cdr;
      this.host = host;
      this.renderer = renderer;
      this.platform = platform;
      this.focusMonitor = focusMonitor;
      this.directionality = directionality;
      this.noAnimation = noAnimation;
      this.nzFormStatusService = nzFormStatusService;
      this.nzFormNoStatusService = nzFormNoStatusService;
      this._nzModuleName = NZ_CONFIG_MODULE_NAME;
      this.nzId = null;
      this.nzSize = 'default';
      this.nzStatus = '';
      this.nzOptionHeightPx = 32;
      this.nzOptionOverflowSize = 8;
      this.nzDropdownClassName = null;
      this.nzDropdownMatchSelectWidth = true;
      this.nzDropdownStyle = null;
      this.nzNotFoundContent = undefined;
      this.nzPlaceHolder = null;
      this.nzPlacement = null;
      this.nzMaxTagCount = Infinity;
      this.nzDropdownRender = null;
      this.nzCustomTemplate = null;
      this.nzSuffixIcon = null;
      this.nzClearIcon = null;
      this.nzRemoveIcon = null;
      this.nzMenuItemSelectedIcon = null;
      this.nzTokenSeparators = [];
      this.nzMaxTagPlaceholder = null;
      this.nzMaxMultipleCount = Infinity;
      this.nzMode = 'default';
      this.nzFilterOption = defaultFilterOption;
      this.compareWith = (o1, o2) => o1 === o2;
      this.nzAllowClear = false;
      this.nzBorderless = false;
      this.nzShowSearch = false;
      this.nzLoading = false;
      this.nzAutoFocus = false;
      this.nzAutoClearSearchValue = true;
      this.nzServerSearch = false;
      this.nzDisabled = false;
      this.nzOpen = false;
      this.nzSelectOnTab = false;
      this.nzBackdrop = false;
      this.nzOptions = [];
      this.nzOnSearch = new EventEmitter();
      this.nzScrollToBottom = new EventEmitter();
      this.nzOpenChange = new EventEmitter();
      this.nzBlur = new EventEmitter();
      this.nzFocus = new EventEmitter();
      this.listOfValue$ = new BehaviorSubject([]);
      this.listOfTemplateItem$ = new BehaviorSubject([]);
      this.listOfTagAndTemplateItem = [];
      this.searchValue = '';
      this.isReactiveDriven = false;
      this.requestId = -1;
      this.isNzDisableFirstChange = true;
      this.onChange = () => {};
      this.onTouched = () => {};
      this.dropDownPosition = 'bottomLeft';
      this.triggerWidth = null;
      this.listOfContainerItem = [];
      this.listOfTopItem = [];
      this.activatedValue = null;
      this.listOfValue = [];
      this.focused = false;
      this.dir = 'ltr';
      this.positions = [];
      this.isMaxLimitReached = false;
      // status
      this.prefixCls = 'ant-select';
      this.statusCls = {};
      this.status = '';
      this.hasFeedback = false;
    }
    writeValue(modelValue) {
      /** https://github.com/angular/angular/issues/14988 **/
      if (this.value !== modelValue) {
        this.value = modelValue;
        const covertModelToList = (model, mode) => {
          if (model === null || model === undefined) {
            return [];
          } else if (mode === 'default') {
            return [model];
          } else {
            return model;
          }
        };
        const listOfValue = covertModelToList(modelValue, this.nzMode);
        this.listOfValue = listOfValue;
        this.listOfValue$.next(listOfValue);
        this.cdr.markForCheck();
      }
    }
    registerOnChange(fn) {
      this.onChange = fn;
    }
    registerOnTouched(fn) {
      this.onTouched = fn;
    }
    setDisabledState(disabled) {
      this.nzDisabled = this.isNzDisableFirstChange && this.nzDisabled || disabled;
      this.isNzDisableFirstChange = false;
      if (this.nzDisabled) {
        this.setOpenState(false);
      }
      this.cdr.markForCheck();
    }
    ngOnChanges(changes) {
      const {
        nzOpen,
        nzDisabled,
        nzOptions,
        nzStatus,
        nzPlacement
      } = changes;
      if (nzOpen) {
        this.onOpenChange();
      }
      if (nzDisabled && this.nzDisabled) {
        this.setOpenState(false);
      }
      if (nzOptions) {
        this.isReactiveDriven = true;
        const listOfOptions = this.nzOptions || [];
        const listOfTransformedItem = listOfOptions.map(item => {
          return {
            template: item.label instanceof TemplateRef ? item.label : null,
            nzTitle: this.getTitle(item.title, item.label),
            nzLabel: typeof item.label === 'string' || typeof item.label === 'number' ? item.label : null,
            nzValue: item.value,
            nzDisabled: item.disabled || false,
            nzHide: item.hide || false,
            nzCustomContent: item.label instanceof TemplateRef,
            groupLabel: item.groupLabel || null,
            type: 'item',
            key: item.key === undefined ? item.value : item.key
          };
        });
        this.listOfTemplateItem$.next(listOfTransformedItem);
      }
      if (nzStatus) {
        this.setStatusStyles(this.nzStatus, this.hasFeedback);
      }
      if (nzPlacement) {
        const {
          currentValue
        } = nzPlacement;
        this.dropDownPosition = currentValue;
        const listOfPlacement = ['bottomLeft', 'topLeft', 'bottomRight', 'topRight'];
        if (currentValue && listOfPlacement.includes(currentValue)) {
          this.positions = [POSITION_MAP[currentValue]];
        } else {
          this.positions = listOfPlacement.map(e => POSITION_MAP[e]);
        }
      }
    }
    ngOnInit() {
      this.nzFormStatusService?.formStatusChanges.pipe(distinctUntilChanged((pre, cur) => {
        return pre.status === cur.status && pre.hasFeedback === cur.hasFeedback;
      }), withLatestFrom(this.nzFormNoStatusService ? this.nzFormNoStatusService.noFormStatus : of(false)), map(([{
        status,
        hasFeedback
      }, noStatus]) => ({
        status: noStatus ? '' : status,
        hasFeedback
      })), takeUntil(this.destroy$)).subscribe(({
        status,
        hasFeedback
      }) => {
        this.setStatusStyles(status, hasFeedback);
      });
      this.focusMonitor.monitor(this.host, true).pipe(takeUntil(this.destroy$)).subscribe(focusOrigin => {
        if (!focusOrigin) {
          this.focused = false;
          this.cdr.markForCheck();
          this.nzBlur.emit();
          Promise.resolve().then(() => {
            this.onTouched();
          });
        } else {
          this.focused = true;
          this.cdr.markForCheck();
          this.nzFocus.emit();
        }
      });
      combineLatest([this.listOfValue$, this.listOfTemplateItem$]).pipe(takeUntil(this.destroy$)).subscribe(([listOfSelectedValue, listOfTemplateItem]) => {
        const listOfTagItem = listOfSelectedValue.filter(() => this.nzMode === 'tags').filter(value => listOfTemplateItem.findIndex(o => this.compareWith(o.nzValue, value)) === -1).map(value => this.listOfTopItem.find(o => this.compareWith(o.nzValue, value)) || this.generateTagItem(value));
        this.listOfTagAndTemplateItem = [...listOfTemplateItem, ...listOfTagItem];
        this.listOfTopItem = this.listOfValue.map(v => [...this.listOfTagAndTemplateItem, ...this.listOfTopItem].find(item => this.compareWith(v, item.nzValue))).filter(item => !!item);
        this.updateListOfContainerItem();
      });
      this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe(direction => {
        this.dir = direction;
        this.cdr.detectChanges();
      });
      this.nzConfigService.getConfigChangeEventForComponent('select').pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.cdr.markForCheck();
      });
      this.dir = this.directionality.value;
      this.ngZone.runOutsideAngular(() => fromEvent(this.host.nativeElement, 'click').pipe(takeUntil(this.destroy$)).subscribe(() => {
        if (this.nzOpen && this.nzShowSearch || this.nzDisabled) {
          return;
        }
        this.ngZone.run(() => this.setOpenState(!this.nzOpen));
      }));
      // Caretaker note: we could've added this listener within the template `(overlayKeydown)="..."`,
      // but with this approach, it'll run change detection on each keyboard click, and also it'll run
      // `markForCheck()` internally, which means the whole component tree (starting from the root and
      // going down to the select component) will be re-checked and updated (if needed).
      // This is safe to do that manually since `setOpenState()` calls `markForCheck()` if needed.
      this.cdkConnectedOverlay.overlayKeydown.pipe(takeUntil(this.destroy$)).subscribe(event => {
        if (event.keyCode === ESCAPE) {
          this.setOpenState(false);
        }
      });
    }
    ngAfterContentInit() {
      if (!this.isReactiveDriven) {
        merge(this.listOfNzOptionGroupComponent.changes, this.listOfNzOptionComponent.changes).pipe(startWith(true), switchMap(() => merge(...[this.listOfNzOptionComponent.changes, this.listOfNzOptionGroupComponent.changes, ...this.listOfNzOptionComponent.map(option => option.changes), ...this.listOfNzOptionGroupComponent.map(option => option.changes)]).pipe(startWith(true))), takeUntil(this.destroy$)).subscribe(() => {
          const listOfOptionInterface = this.listOfNzOptionComponent.toArray().map(item => {
            const {
              template,
              nzLabel,
              nzValue,
              nzKey,
              nzDisabled,
              nzHide,
              nzCustomContent,
              groupLabel
            } = item;
            return {
              template,
              nzLabel,
              nzValue,
              nzDisabled,
              nzHide,
              nzCustomContent,
              groupLabel,
              nzTitle: this.getTitle(item.nzTitle, item.nzLabel),
              type: 'item',
              key: nzKey === undefined ? nzValue : nzKey
            };
          });
          this.listOfTemplateItem$.next(listOfOptionInterface);
          this.cdr.markForCheck();
        });
      }
    }
    ngOnDestroy() {
      cancelRequestAnimationFrame(this.requestId);
      this.focusMonitor.stopMonitoring(this.host);
    }
    setStatusStyles(status, hasFeedback) {
      this.status = status;
      this.hasFeedback = hasFeedback;
      this.cdr.markForCheck();
      // render status if nzStatus is set
      this.statusCls = getStatusClassNames(this.prefixCls, status, hasFeedback);
      Object.keys(this.statusCls).forEach(status => {
        if (this.statusCls[status]) {
          this.renderer.addClass(this.host.nativeElement, status);
        } else {
          this.renderer.removeClass(this.host.nativeElement, status);
        }
      });
    }
    getTitle(title, label) {
      let rawTitle = undefined;
      if (title === undefined) {
        if (typeof label === 'string' || typeof label === 'number') {
          rawTitle = label.toString();
        }
      } else if (typeof title === 'string' || typeof title === 'number') {
        rawTitle = title.toString();
      }
      return rawTitle;
    }
    static {
      this.ɵfac = function NzSelectComponent_Factory(t) {
        return new (t || NzSelectComponent)(i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i1$1.NzDestroyService), i0.ɵɵdirectiveInject(i2$2.NzConfigService), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i3.Platform), i0.ɵɵdirectiveInject(i1$4.FocusMonitor), i0.ɵɵdirectiveInject(i5.Directionality, 8), i0.ɵɵdirectiveInject(i1$5.NzNoAnimationDirective, 9), i0.ɵɵdirectiveInject(i7.NzFormStatusService, 8), i0.ɵɵdirectiveInject(i7.NzFormNoStatusService, 8));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzSelectComponent,
        selectors: [["nz-select"]],
        contentQueries: function NzSelectComponent_ContentQueries(rf, ctx, dirIndex) {
          if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, NzOptionComponent, 5);
            i0.ɵɵcontentQuery(dirIndex, NzOptionGroupComponent, 5);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.listOfNzOptionComponent = _t);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.listOfNzOptionGroupComponent = _t);
          }
        },
        viewQuery: function NzSelectComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(CdkOverlayOrigin, 7, ElementRef);
            i0.ɵɵviewQuery(CdkConnectedOverlay, 7);
            i0.ɵɵviewQuery(NzSelectTopControlComponent, 7);
            i0.ɵɵviewQuery(NzOptionGroupComponent, 7, ElementRef);
            i0.ɵɵviewQuery(NzSelectTopControlComponent, 7, ElementRef);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.originElement = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.cdkConnectedOverlay = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.nzSelectTopControlComponent = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.nzOptionGroupComponentElement = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.nzSelectTopControlComponentElement = _t.first);
          }
        },
        hostAttrs: [1, "ant-select"],
        hostVars: 26,
        hostBindings: function NzSelectComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵclassProp("ant-select-in-form-item", !!ctx.nzFormStatusService)("ant-select-lg", ctx.nzSize === "large")("ant-select-sm", ctx.nzSize === "small")("ant-select-show-arrow", ctx.nzShowArrow)("ant-select-disabled", ctx.nzDisabled)("ant-select-show-search", (ctx.nzShowSearch || ctx.nzMode !== "default") && !ctx.nzDisabled)("ant-select-allow-clear", ctx.nzAllowClear)("ant-select-borderless", ctx.nzBorderless)("ant-select-open", ctx.nzOpen)("ant-select-focused", ctx.nzOpen || ctx.focused)("ant-select-single", ctx.nzMode === "default")("ant-select-multiple", ctx.nzMode !== "default")("ant-select-rtl", ctx.dir === "rtl");
          }
        },
        inputs: {
          nzId: "nzId",
          nzSize: "nzSize",
          nzStatus: "nzStatus",
          nzOptionHeightPx: "nzOptionHeightPx",
          nzOptionOverflowSize: "nzOptionOverflowSize",
          nzDropdownClassName: "nzDropdownClassName",
          nzDropdownMatchSelectWidth: "nzDropdownMatchSelectWidth",
          nzDropdownStyle: "nzDropdownStyle",
          nzNotFoundContent: "nzNotFoundContent",
          nzPlaceHolder: "nzPlaceHolder",
          nzPlacement: "nzPlacement",
          nzMaxTagCount: "nzMaxTagCount",
          nzDropdownRender: "nzDropdownRender",
          nzCustomTemplate: "nzCustomTemplate",
          nzSuffixIcon: "nzSuffixIcon",
          nzClearIcon: "nzClearIcon",
          nzRemoveIcon: "nzRemoveIcon",
          nzMenuItemSelectedIcon: "nzMenuItemSelectedIcon",
          nzTokenSeparators: "nzTokenSeparators",
          nzMaxTagPlaceholder: "nzMaxTagPlaceholder",
          nzMaxMultipleCount: "nzMaxMultipleCount",
          nzMode: "nzMode",
          nzFilterOption: "nzFilterOption",
          compareWith: "compareWith",
          nzAllowClear: "nzAllowClear",
          nzBorderless: "nzBorderless",
          nzShowSearch: "nzShowSearch",
          nzLoading: "nzLoading",
          nzAutoFocus: "nzAutoFocus",
          nzAutoClearSearchValue: "nzAutoClearSearchValue",
          nzServerSearch: "nzServerSearch",
          nzDisabled: "nzDisabled",
          nzOpen: "nzOpen",
          nzSelectOnTab: "nzSelectOnTab",
          nzBackdrop: "nzBackdrop",
          nzOptions: "nzOptions",
          nzShowArrow: "nzShowArrow"
        },
        outputs: {
          nzOnSearch: "nzOnSearch",
          nzScrollToBottom: "nzScrollToBottom",
          nzOpenChange: "nzOpenChange",
          nzBlur: "nzBlur",
          nzFocus: "nzFocus"
        },
        exportAs: ["nzSelect"],
        standalone: true,
        features: [i0.ɵɵProvidersFeature([NzDestroyService, {
          provide: NG_VALUE_ACCESSOR,
          useExisting: forwardRef(() => NzSelectComponent),
          multi: true
        }]), i0.ɵɵNgOnChangesFeature, i0.ɵɵStandaloneFeature],
        decls: 5,
        vars: 25,
        consts: [["cdkOverlayOrigin", "", 3, "nzId", "open", "disabled", "mode", "nzNoAnimation", "maxTagPlaceholder", "removeIcon", "placeHolder", "maxTagCount", "customTemplate", "tokenSeparators", "showSearch", "autofocus", "listOfTopItem", "inputValueChange", "tokenize", "deleteItem", "keydown"], ["origin", "cdkOverlayOrigin"], [3, "showArrow", "loading", "search", "suffixIcon", "feedbackIcon", "nzMaxMultipleCount", "listOfValue", "isMaxTagCountSet", 4, "ngIf"], [3, "clearIcon", "clear", 4, "ngIf"], ["cdkConnectedOverlay", "", "nzConnectedOverlay", "", 3, "cdkConnectedOverlayHasBackdrop", "cdkConnectedOverlayMinWidth", "cdkConnectedOverlayWidth", "cdkConnectedOverlayOrigin", "cdkConnectedOverlayTransformOriginOn", "cdkConnectedOverlayPanelClass", "cdkConnectedOverlayOpen", "cdkConnectedOverlayPositions", "overlayOutsideClick", "detach", "positionChange"], [3, "showArrow", "loading", "search", "suffixIcon", "feedbackIcon", "nzMaxMultipleCount", "listOfValue", "isMaxTagCountSet"], ["feedbackIconTpl", ""], [3, "status", 4, "ngIf"], [3, "status"], [3, "clearIcon", "clear"], [3, "ngStyle", "itemSize", "maxItemLength", "matchWidth", "nzNoAnimation", "listOfContainerItem", "menuItemSelectedIcon", "notFoundContent", "activatedValue", "listOfSelectedValue", "dropdownRender", "compareWith", "mode", "isMaxLimitReached", "keydown", "itemClick", "scrollToBottom"]],
        template: function NzSelectComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "nz-select-top-control", 0, 1);
            i0.ɵɵlistener("inputValueChange", function NzSelectComponent_Template_nz_select_top_control_inputValueChange_0_listener($event) {
              return ctx.onInputValueChange($event);
            })("tokenize", function NzSelectComponent_Template_nz_select_top_control_tokenize_0_listener($event) {
              return ctx.onTokenSeparate($event);
            })("deleteItem", function NzSelectComponent_Template_nz_select_top_control_deleteItem_0_listener($event) {
              return ctx.onItemDelete($event);
            })("keydown", function NzSelectComponent_Template_nz_select_top_control_keydown_0_listener($event) {
              return ctx.onKeyDown($event);
            });
            i0.ɵɵelementEnd();
            i0.ɵɵtemplate(2, NzSelectComponent_nz_select_arrow_2_Template, 3, 8, "nz-select-arrow", 2)(3, NzSelectComponent_nz_select_clear_3_Template, 1, 1, "nz-select-clear", 3)(4, NzSelectComponent_ng_template_4_Template, 1, 24, "ng-template", 4);
            i0.ɵɵlistener("overlayOutsideClick", function NzSelectComponent_Template_ng_template_overlayOutsideClick_4_listener($event) {
              return ctx.onClickOutside($event);
            })("detach", function NzSelectComponent_Template_ng_template_detach_4_listener() {
              return ctx.setOpenState(false);
            })("positionChange", function NzSelectComponent_Template_ng_template_positionChange_4_listener($event) {
              return ctx.onPositionChange($event);
            });
          }
          if (rf & 2) {
            const _r0 = i0.ɵɵreference(1);
            i0.ɵɵproperty("nzId", ctx.nzId)("open", ctx.nzOpen)("disabled", ctx.nzDisabled)("mode", ctx.nzMode)("@.disabled", !!(ctx.noAnimation == null ? null : ctx.noAnimation.nzNoAnimation))("nzNoAnimation", ctx.noAnimation == null ? null : ctx.noAnimation.nzNoAnimation)("maxTagPlaceholder", ctx.nzMaxTagPlaceholder)("removeIcon", ctx.nzRemoveIcon)("placeHolder", ctx.nzPlaceHolder)("maxTagCount", ctx.nzMaxTagCount)("customTemplate", ctx.nzCustomTemplate)("tokenSeparators", ctx.nzTokenSeparators)("showSearch", ctx.nzShowSearch)("autofocus", ctx.nzAutoFocus)("listOfTopItem", ctx.listOfTopItem);
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("ngIf", ctx.nzShowArrow || ctx.hasFeedback && !!ctx.status || ctx.isMaxTagCountSet);
            i0.ɵɵadvance(1);
            i0.ɵɵproperty("ngIf", ctx.nzAllowClear && !ctx.nzDisabled && ctx.listOfValue.length);
            i0.ɵɵadvance(1);
            i0.ɵɵproperty("cdkConnectedOverlayHasBackdrop", ctx.nzBackdrop)("cdkConnectedOverlayMinWidth", ctx.nzDropdownMatchSelectWidth ? null : ctx.triggerWidth)("cdkConnectedOverlayWidth", ctx.nzDropdownMatchSelectWidth ? ctx.triggerWidth : null)("cdkConnectedOverlayOrigin", _r0)("cdkConnectedOverlayTransformOriginOn", ".ant-select-dropdown")("cdkConnectedOverlayPanelClass", ctx.nzDropdownClassName)("cdkConnectedOverlayOpen", ctx.nzOpen)("cdkConnectedOverlayPositions", ctx.positions);
          }
        },
        dependencies: [NzSelectTopControlComponent, CdkOverlayOrigin, NzNoAnimationDirective, NzSelectArrowComponent, NgIf, NzFormPatchModule, i7.NzFormItemFeedbackIconComponent, NzSelectClearComponent, CdkConnectedOverlay, NzOverlayModule, i8.NzConnectedOverlayDirective, NzOptionContainerComponent, NgStyle],
        encapsulation: 2,
        data: {
          animation: [slideMotion]
        },
        changeDetection: 0
      });
    }
  }
  __decorate([WithConfig()], NzSelectComponent.prototype, "nzSuffixIcon", void 0);
  __decorate([InputBoolean()], NzSelectComponent.prototype, "nzAllowClear", void 0);
  __decorate([WithConfig(), InputBoolean()], NzSelectComponent.prototype, "nzBorderless", void 0);
  __decorate([InputBoolean()], NzSelectComponent.prototype, "nzShowSearch", void 0);
  __decorate([InputBoolean()], NzSelectComponent.prototype, "nzLoading", void 0);
  __decorate([InputBoolean()], NzSelectComponent.prototype, "nzAutoFocus", void 0);
  __decorate([InputBoolean()], NzSelectComponent.prototype, "nzAutoClearSearchValue", void 0);
  __decorate([InputBoolean()], NzSelectComponent.prototype, "nzServerSearch", void 0);
  __decorate([InputBoolean()], NzSelectComponent.prototype, "nzDisabled", void 0);
  __decorate([InputBoolean()], NzSelectComponent.prototype, "nzOpen", void 0);
  __decorate([InputBoolean()], NzSelectComponent.prototype, "nzSelectOnTab", void 0);
  __decorate([WithConfig(), InputBoolean()], NzSelectComponent.prototype, "nzBackdrop", void 0);
  return NzSelectComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzSelectModule = /*#__PURE__*/(() => {
  class NzSelectModule {
    static {
      this.ɵfac = function NzSelectModule_Factory(t) {
        return new (t || NzSelectModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: NzSelectModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [NzSelectComponent, NzOptionContainerComponent, NzOptionItemComponent, NzSelectTopControlComponent, NzSelectSearchComponent, NzSelectItemComponent, NzSelectClearComponent, NzSelectArrowComponent, NzSelectPlaceholderComponent, NzOptionItemGroupComponent]
      });
    }
  }
  return NzSelectModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NzOptionComponent, NzOptionContainerComponent, NzOptionGroupComponent, NzOptionItemComponent, NzOptionItemGroupComponent, NzSelectArrowComponent, NzSelectClearComponent, NzSelectComponent, NzSelectItemComponent, NzSelectModule, NzSelectPlaceholderComponent, NzSelectSearchComponent, NzSelectTopControlComponent };
